import { shouldShowAffiliateLinkModalAtom } from '@/atoms/affiliateLinkAtoms';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useAtom, useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useEffect } from 'react';

import { trackReferralLinkCopied } from '@/lib/analytics/scribe/referral';
import { isReferralProgramAvailableForLocale } from '@/utils/i18n/referralProgramLocale';
import { isAffiliateLinkModalOpenAtom } from '@heidi/growth';
import { useIntl } from 'react-intl';

type AffiliateLinkModalProps = {
  className?: string;
  isOpen?: boolean;
};

const AffiliateLinkModalImpl = dynamic(
  () =>
    import('./affiliate-link-modal-impl').then((module) => module.AffiliateLinkModalImpl),
  { ssr: false }
);

const AffiliateLinkModal = ({ className, isOpen }: AffiliateLinkModalProps) => {
  const intl = useIntl();
  const [isManualOpen, setIsManualOpen] = useAtom(isAffiliateLinkModalOpenAtom);
  const setShouldShowAffiliateLinkModal = useSetAtom(shouldShowAffiliateLinkModalAtom);
  const {
    isOpen: isQueueOpen,
    handleClose: dequeueQueuedModal,
    closeWithOptions,
  } = useQueuedModal(ModalId.AFFILIATE_LINK);

  const isReferralAvailable = isReferralProgramAvailableForLocale(intl.locale);
  const shouldBeOpen = isOpen !== undefined ? isOpen : isManualOpen || isQueueOpen;

  const handleClose = () => {
    setIsManualOpen(false);
    setShouldShowAffiliateLinkModal(false);
    dequeueQueuedModal();
  };

  // Locale guard must dequeue — returning null alone leaves AFFILIATE_LINK at the
  // head of the launch queue and blocks every lower-priority modal for the session.
  useEffect(() => {
    if (isReferralAvailable) return;

    setIsManualOpen(false);
    setShouldShowAffiliateLinkModal(false);
    if (isQueueOpen) {
      closeWithOptions({ skipPacing: true, increment_count: false });
    }
  }, [
    isReferralAvailable,
    isQueueOpen,
    closeWithOptions,
    setIsManualOpen,
    setShouldShowAffiliateLinkModal,
  ]);

  if (!shouldBeOpen || !isReferralAvailable) return null;

  return (
    <AffiliateLinkModalImpl
      className={className}
      onClose={handleClose}
      onReferralLinkCopied={() => trackReferralLinkCopied()}
    />
  );
};

export default AffiliateLinkModal;
